/* eslint-disable jsx-a11y/accessible-emoji */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { BILLING_DECIMAL_PLACES } from '../../constants/billingConstants';
import { connect } from 'react-redux';
import withBase from '../../hoc/withBase';

import Button from '@material-ui/core/Button/';
import Typography from '@material-ui/core/Typography'
import LinearProgress from '@material-ui/core/LinearProgress';
import Chip from '@material-ui/core/Chip'
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Box from '@material-ui/core/Box';
import Divider from '@material-ui/core/Divider';
import Modal from '@material-ui/core/Modal';
import Backdrop from '@material-ui/core/Backdrop';
import Fade from '@material-ui/core/Fade';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core/styles';

// Icons
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import AssignmentIcon from '@material-ui/icons/Assignment';
import PublishIcon from '@material-ui/icons/Publish';
import CircularProgress from '@material-ui/core/CircularProgress';

import GridContainer from '../../components/Grid/GridContainer';
import GridItem from '../../components/Grid/GridItem';
import Card from '../../components/Card/Card';
import CardHeader from '../../components/Card/CardHeader';
import CardBody from '../../components/Card/CardBody';

import SearchTable from '../../components/Table/Table';

import * as actions from '../../store/actions/invoiceDownloads'
import { findUnits } from '../../helpers/helper';
import axiosInstance from '../../axios';

// Import centralized theme
import appTheme from '../../themes/theme';

const useStyles = makeStyles((theme) => ({
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: appTheme.spacing.md,
    flexWrap: 'wrap',
    marginBottom: appTheme.spacing.lg,
  },
  chipsRow: {
    display: 'flex',
    gap: appTheme.spacing.sm,
    flexWrap: 'wrap',
  },
  sectionHint: {
    background: appTheme.colors.neutral.background,
    border: `1px solid ${appTheme.colors.neutral.border}`,
    borderRadius: appTheme.borderRadius.small,
    padding: appTheme.spacing.md,
    marginTop: appTheme.spacing.md,
  },
  progressRow: {
    marginTop: appTheme.spacing.md,
  },
  actionsRow: {
    display: 'flex',
    gap: appTheme.spacing.sm,
    flexWrap: 'wrap',
    marginTop: appTheme.spacing.md,
  },
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    maxHeight: '90vh',
    width: '100vw',
  },
  modalPaper: {
    backgroundColor: appTheme.colors.neutral.white,
    borderRadius: appTheme.borderRadius.medium,
    boxShadow: appTheme.shadows.elevated,
    padding: 0,
    width: 'min(1200px, 98vw)',
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: appTheme.spacing.lg,
    background: `linear-gradient(135deg, ${appTheme.colors.primary.main} 0%, ${appTheme.colors.primary.dark} 100%)`,
    color: appTheme.colors.primary.contrast,
    borderTopLeftRadius: appTheme.borderRadius.medium,
    borderTopRightRadius: appTheme.borderRadius.medium,
  },
  modalBody: {
    padding: appTheme.spacing.lg,
  },
  rotating: {
    animation: '$spin 2s linear infinite',
  },
  '@keyframes spin': {
    '0%': {
      transform: 'rotate(0deg)',
    },
    '100%': {
      transform: 'rotate(360deg)',
    },
  },
}));

const BillingRunDetails = (props) => {
  const classes = useStyles();
  const processId = props.match.params.processId || props.match.params.runId;

  const [invoiceDownloadProgress, setInvoiceDownloadProgress] = useState(null);
  const [allInvoices, setAllInvoices] = useState([]);

  // Modal state for invoice details
  const [invoiceDetailsModalOpen, setInvoiceDetailsModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // Modal state for processed billing details
  const [billingDetailsModalOpen, setBillingDetailsModalOpen] = useState(false);
  const [selectedBillingInvoice, setSelectedBillingInvoice] = useState(null);

  // Modal state for re-process actions
  const [reprocessModalOpen, setReprocessModalOpen] = useState(false);
  const [reprocessOption, setReprocessOption] = useState('all'); // 'all', 'failed', 'selected'
  const [selectedInvoicesForReprocess, setSelectedInvoicesForReprocess] = useState([]);

  // Tab state: 0 = Step 1, 1 = Step 2, 2 = Step 3
  const [tab, setTab] = useState(0);

  // Step 3 export state
  const [exports, setExports] = useState([]);
  const [exportInProgress, setExportInProgress] = useState(false);
  const [downloadingExportId, setDownloadingExportId] = useState(null);
  const exportPollTimer = useRef(null);

  const formatInvoiceDate = (invoiceDate) => {
    // invoiceDate format: YYYYMM (e.g., 202405)
    if (!invoiceDate || invoiceDate.toString().length !== 6) return invoiceDate;
    const dateStr = invoiceDate.toString();
    const year = dateStr.substring(0, 4);
    const month = dateStr.substring(4, 6);
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthIndex = parseInt(month, 10) - 1;
    return monthNames[monthIndex] ? `${monthNames[monthIndex]} ${year}` : invoiceDate;
  }

  const refresh = () => {
    props.getInvoiceDownloadProcessDetails(processId, (err, data) => {
      if (!err) {
        const progress = data.data.invoiceDownloadProgress;
        setInvoiceDownloadProgress(progress);
        setAllInvoices(data.data.allInvoices);

        // Fetch exports if billing is completed
        if (progress && ['COMPLETED', 'COMPLETED_WITH_ERRORS'].includes(progress.billingProcessStatus)) {
          props.getBillingExports(processId, (err, data) => {
            if (!err) {
              setExports(data.data || []);

              // Check if any export is in progress
              const hasInProgress = (data.data || []).some(exp =>
                exp.status === 'PENDING' || exp.status === 'PROCESSING'
              );
              setExportInProgress(hasInProgress);
            }
          });
        }
      }
    });
  }

  useEffect(() => {
    refresh();

    const downloadStatus = invoiceDownloadProgress?.downloadStatus;
    const billingStatus = invoiceDownloadProgress?.billingProcessStatus;

    const shouldPoll =
      (downloadStatus && ['PENDING', 'STARTED'].includes(downloadStatus)) ||
      (billingStatus && ['PENDING', 'STARTED'].includes(billingStatus)) ||
      exportInProgress;

    if (!shouldPoll) return;

    // Only create the interval when there is actually something in progress
    const poll = setInterval(() => {
      refresh();
    }, 15000);

    return () => {
      clearInterval(poll);
      if (exportPollTimer.current) clearInterval(exportPollTimer.current);
    }
  }, [processId, invoiceDownloadProgress?.downloadStatus, invoiceDownloadProgress?.billingProcessStatus, exportInProgress])

  const step1Totals = useMemo(() => {
    const completed = invoiceDownloadProgress?.completedDownload?.length || 0;
    const errors = invoiceDownloadProgress?.errorDownload?.length || 0;
    const total = completed + errors;
    return { completed, errors, total };
  }, [invoiceDownloadProgress]);

  const step1Percent = useMemo(() => {
    // We don't have total operators in the process doc (yet). Visualise progress using completed+errors.
    // If there are none yet, show 0.
    if (!step1Totals.total) return 0;
    return 100;
  }, [step1Totals]);

  const step2Totals = useMemo(() => {
    const completed = invoiceDownloadProgress?.completedBillingProcess?.length || 0;
    const errors = invoiceDownloadProgress?.errorBillingProcess?.length || 0;
    const total = completed + errors;
    return { completed, errors, total };
  }, [invoiceDownloadProgress]);

  const step2Percent = useMemo(() => {
    // Count invoices that need processing (status=COMPLETED with summary data or NO_INVOICE)
    const readyForProcessing = allInvoices.filter(
      inv => (inv.status === 'COMPLETED' && inv.summary && inv.summary.length > 0) || inv.status === 'NO_INVOICE'
    ).length;

    if (!readyForProcessing) return 0;
    const processed = step2Totals.total;
    return Math.round((processed / readyForProcessing) * 100);
  }, [allInvoices, step2Totals]);

  const invoicesHeadCells = useMemo(() => ([
    { id: 'name', label: 'Name', isSearchable: true },
    { id: 'status', label: 'Step 1 Status', isSearchable: true },
    { id: 'operatorId', label: 'Operator Id', isSearchable: true },
    // { id: 'totalAmount', label: 'Total Amount', isSearchable: true, type: 'Float' },
    { id: 'createdAt', label: 'Created', isSearchable: false, type: 'Date' }
  ]), [])

  const invoiceSummaryHeadCells = useMemo(() => ([
    { id: 'itemName', label: 'Item Name', isSearchable: true },
    // { id: 'unitPrice', label: 'Unit Price (USD)', isSearchable: false },
    { id: 'quantity', label: 'Quantity', isSearchable: false },
    // { id: 'amount', label: 'Amount (USD)', isSearchable: false },
  ]), [])

  const handleInvoiceRowClick = (event, row) => {
    const invoice = allInvoices.find(inv => inv.operatorId === row[2]);
    if (invoice) {
      setSelectedInvoice(invoice);
      setInvoiceDetailsModalOpen(true);
    }
  }

  const canRunStep2 = useMemo(() => {
    if (!invoiceDownloadProgress) return false; // Still loading
    const s1 = invoiceDownloadProgress?.downloadStatus;
    return ['COMPLETED', 'COMPLETED_WITH_ERRORS'].includes(s1);
  }, [invoiceDownloadProgress]);

  const startBillingProcessing = () => {
    // Call backend API to start processing
    props.startBillingProcessing(processId, (err, data) => {
      if (err) {
        console.error('Failed to start billing processing:', err);
        alert('Failed to start billing processing: ' + (err.message || 'Unknown error'));
      } else {
        console.log('Billing processing started:', data);
        // Refresh to get updated status
        refresh();
      }
    });
  };

  const startBillingExport = () => {
    setExportInProgress(true);

    // Get user info from props or local storage
    const userInfo = props.user || {
      _id: 'system',
      name: 'System User',
      email: 'system@thinxtra.com'
    };

    props.startBillingExport(processId, {
      userId: userInfo._id || 'system',
      userName: userInfo.name || 'Unknown',
      email: userInfo.email || ''
    }, (err, data) => {
      if (err) {
        console.error('Failed to start export:', err);
        alert('Failed to start export: ' + (err.response?.data?.error || err.message || 'Unknown error'));
        setExportInProgress(false);
      } else {
        console.log('Export started:', data);
        // Refresh to get updated exports list
        setTimeout(() => refresh(), 1000);
      }
    });
  };

  const downloadExport = async (exportItem) => {
    if (!exportItem || !exportItem.zipFile) {
      alert('Export file not available');
      return;
    }

    setDownloadingExportId(exportItem._id);
    try {
      const response = await axiosInstance.get(
        `/api/v1/billing-exports/${exportItem._id}/download`,
        { responseType: 'blob' }
      );

      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = exportItem.zipFile.fileName || 'billing_export.zip';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Download failed: ' + err.message);
    } finally {
      setDownloadingExportId(null);
    }
  };

  const handleBillingRowClick = (event, rowData) => {
    console.log('Billing row clicked:', { rowData });
    // rowData is the array of values for this row, need to find the invoice by operatorId
    const operatorId = rowData[0]; // First column is operatorId
    const invoice = allInvoices.find(inv => inv.operatorId === operatorId);
    console.log('Selected invoice:', invoice);
    if (invoice) {
      setSelectedBillingInvoice(invoice);
      setBillingDetailsModalOpen(true);
      console.log('Modal should open now');
    } else {
      console.log('No invoice found with operatorId:', operatorId);
    }
  }

  const handleAbortProcessing = () => {
    if (!window.confirm('Are you sure you want to abort processing? All pending jobs will be cancelled.')) {
      return;
    }

    props.abortBillingProcessing(processId, (err, response) => {
      if (err) {
        props.showNotification({
          color: 'danger',
          message: `Failed to abort processing: ${err.message || 'Unknown error'}`,
          time: 5000
        });
      } else {
        props.showNotification({
          color: 'success',
          message: 'Processing aborted. All pending jobs cancelled.',
          time: 3000
        });
        // Refresh the data
        setTimeout(refresh, 1000);
      }
    });
  }

  const handleReprocessSingle = (invoice) => {
    // Use operatorId for robust backend lookup
    if (props.reprocessSingleInvoiceByOperator) {
      props.reprocessSingleInvoiceByOperator(processId, invoice.operatorId, (err, response) => {
        if (err) {
          props.showNotification({
            color: 'danger',
            message: `Failed to re-process invoice: ${err.message || 'Unknown error'}`,
            time: 5000
          });
        } else {
          props.showNotification({
            color: 'success',
            message: `Re-processing invoice for ${invoice.operatorId}...`,
            time: 3000
          });
          // Refresh the data
          setTimeout(refresh, 1000);
        }
      });
    } else {
      // fallback for dev: show error
      props.showNotification({
        color: 'danger',
        message: 'Reprocess action not available (missing Redux action).',
        time: 5000
      });
    }
  }

  const openReprocessModal = () => {
    setReprocessModalOpen(true);
  }

  const handleReprocessConfirm = () => {
    const options = { mode: reprocessOption };

    props.reprocessBilling(processId, options, (err, response) => {
      if (err) {
        props.showNotification({
          color: 'danger',
          message: `Failed to start re-processing: ${err.message || 'Unknown error'}`,
          time: 5000
        });
      } else {
        props.showNotification({
          color: 'success',
          message: `Re-processing ${reprocessOption} invoices...`,
          time: 3000
        });
        setReprocessModalOpen(false);
        // Refresh the data
        setTimeout(refresh, 1000);
      }
    });
  }

  const invoiceDetailsModal = selectedInvoice && (
    <Modal
      className={classes.modal}
      open={invoiceDetailsModalOpen}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
      onClose={() => setInvoiceDetailsModalOpen(false)}
    >
      <Fade in={invoiceDetailsModalOpen}>
        <div className={classes.modalPaper}>
          <div className={classes.modalHeader}>
            <div>
              <Typography variant="h6" style={{ fontWeight: appTheme.typography.fontWeight.bold, marginBottom: appTheme.spacing.xs }}>
                Invoice Details
              </Typography>
              <Typography variant="body2" style={{ opacity: 0.9 }}>
                {selectedInvoice.name} ({selectedInvoice.operatorId})
              </Typography>
            </div>
            <IconButton
              size="small"
              onClick={() => setInvoiceDetailsModalOpen(false)}
              style={{ color: appTheme.colors.primary.contrast }}
            >
              <CloseIcon />
            </IconButton>
          </div>

          <div className={classes.modalBody}>
            {/* Invoice Summary Information */}
            <TableContainer component={Paper} variant="outlined" style={{ marginBottom: appTheme.spacing.lg }}>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell style={{ fontWeight: appTheme.typography.fontWeight.semibold, width: '40%' }}>
                      Name
                    </TableCell>
                    <TableCell>{selectedInvoice.name}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ fontWeight: appTheme.typography.fontWeight.semibold }}>
                      Operator ID
                    </TableCell>
                    <TableCell style={{ fontFamily: 'monospace' }}>{selectedInvoice.operatorId}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ fontWeight: appTheme.typography.fontWeight.semibold }}>
                      Status
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={selectedInvoice.status}
                        color={selectedInvoice.status === 'COMPLETED' ? 'primary' : 'default'}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ fontWeight: appTheme.typography.fontWeight.semibold }}>
                      Registered Subscribers
                    </TableCell>
                    <TableCell>{selectedInvoice.registered || 0}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ fontWeight: appTheme.typography.fontWeight.semibold }}>
                      Activated Subscribers
                    </TableCell>
                    <TableCell>{selectedInvoice.activated || 0}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ fontWeight: appTheme.typography.fontWeight.semibold }}>
                      Invoice Date
                    </TableCell>
                    <TableCell>{formatInvoiceDate(selectedInvoice.invoiceDate)}</TableCell>
                  </TableRow>
                  {/* <TableRow>
                    <TableCell style={{ fontWeight: appTheme.typography.fontWeight.semibold }}>
                      Total Amount (USD)
                    </TableCell>
                    <TableCell style={{
                      fontSize: appTheme.typography.fontSize.h6,
                      fontWeight: appTheme.typography.fontWeight.bold,
                      color: appTheme.colors.primary.main
                    }}>
                      $ {(selectedInvoice.total || 0).toFixed(BILLING_DECIMAL_PLACES)}
                    </TableCell>
                  </TableRow> */}
                  <TableRow>
                    <TableCell style={{ fontWeight: appTheme.typography.fontWeight.semibold }}>
                      Created At
                    </TableCell>
                    <TableCell>{new Date(selectedInvoice.createdAt).toLocaleString()}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {/* Line Items / Summary */}
            {selectedInvoice.summary && selectedInvoice.summary.length > 0 ? (
              <>
                <Typography variant="h6" style={{
                  marginBottom: appTheme.spacing.md,
                  fontWeight: appTheme.typography.fontWeight.semibold,
                  color: appTheme.colors.neutral.darkText
                }}>
                  Line Items
                </Typography>
                <SearchTable
                  rows={selectedInvoice.summary.map(summary => [
                    summary.billItemName,
                    // summary.unitPrice ? `$ ${summary.unitPrice.toFixed(BILLING_DECIMAL_PLACES)}` : "N/A",
                    summary.quantity && !isNaN(summary.quantity)
                      ? `${summary.quantity.toFixed(BILLING_DECIMAL_PLACES)}`
                      : "N/A",
                    // summary.amount ? `$ ${summary.amount.toFixed(BILLING_DECIMAL_PLACES)}` : "N/A"
                  ])}
                  headCells={invoiceSummaryHeadCells}
                  name="Invoice Line Items"
                />
              </>
            ) : (
              <Box p={3} textAlign="center" style={{ background: appTheme.colors.neutral.background, borderRadius: appTheme.borderRadius.small }}>
                <Typography variant="body2" color="textSecondary">
                  No line item details available for this invoice.
                </Typography>
              </Box>
            )}

            <Box mt={3} display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                color="primary"
                onClick={() => setInvoiceDetailsModalOpen(false)}
              >
                Close
              </Button>
            </Box>
          </div>
        </div>
      </Fade>
    </Modal>
  );

  const step1Status = invoiceDownloadProgress?.downloadStatus;
  const step1StatusChipColor = useMemo(() => {
    if (!step1Status) return 'default'
    if (['COMPLETED'].includes(step1Status)) return 'primary'
    if (['COMPLETED_WITH_ERRORS'].includes(step1Status)) return 'secondary'
    if (['FAILED'].includes(step1Status)) return 'default'
    return 'default'
  }, [step1Status]);

  return (
    <>
      {invoiceDetailsModal}

      {/* Billing Details Modal */}
      {selectedBillingInvoice && (
        <Modal
          className={classes.modal}
          open={billingDetailsModalOpen}
          closeAfterTransition
          BackdropComponent={Backdrop}
          BackdropProps={{ timeout: 500 }}
          onClose={() => setBillingDetailsModalOpen(false)}
        >
          <Fade in={billingDetailsModalOpen}>
            <div className={classes.modalPaper}>
              <div className={classes.modalHeader}>
                <div>
                  <Typography variant="h6" style={{ fontWeight: appTheme.typography.fontWeight.bold, marginBottom: appTheme.spacing.xs }}>
                    Processed Billing Details
                  </Typography>
                  <Typography variant="body2" style={{ opacity: 0.9 }}>
                    {selectedBillingInvoice.name} ({selectedBillingInvoice.operatorId})
                  </Typography>
                </div>
                <IconButton
                  size="small"
                  onClick={() => setBillingDetailsModalOpen(false)}
                  style={{ color: appTheme.colors.primary.contrast }}
                >
                  <CloseIcon />
                </IconButton>
              </div>

              <div className={classes.modalBody}>
                {/* Invoice Summary Information */}
                <TableContainer component={Paper} variant="outlined" style={{ marginBottom: appTheme.spacing.lg }}>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell style={{ fontWeight: appTheme.typography.fontWeight.semibold, width: '40%' }}>
                          Name
                        </TableCell>
                        <TableCell>{selectedBillingInvoice.name}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ fontWeight: appTheme.typography.fontWeight.semibold }}>
                          Operator ID
                        </TableCell>
                        <TableCell>{selectedBillingInvoice.operatorId}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ fontWeight: appTheme.typography.fontWeight.semibold }}>
                          Download Status
                        </TableCell>
                        <TableCell>{selectedBillingInvoice.status || 'PENDING'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ fontWeight: appTheme.typography.fontWeight.semibold }}>
                          Processing Status
                        </TableCell>
                        <TableCell>{selectedBillingInvoice.processedBillingStatus || 'PENDING'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ fontWeight: appTheme.typography.fontWeight.semibold }}>
                          Processed Total
                        </TableCell>
                        <TableCell>
                          ${selectedBillingInvoice.processedBillingTotal?.toFixed(BILLING_DECIMAL_PLACES) || '0.00000'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ fontWeight: appTheme.typography.fontWeight.semibold }}>
                          Created At
                        </TableCell>
                        <TableCell>{new Date(selectedBillingInvoice.createdAt).toLocaleString()}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Processed Billing Line Items */}
                {selectedBillingInvoice.processedBilling && selectedBillingInvoice.processedBilling.length > 0 ? (
                  <>
                    <Typography variant="h6" style={{
                      marginBottom: appTheme.spacing.md,
                      fontWeight: appTheme.typography.fontWeight.semibold,
                      color: appTheme.colors.neutral.darkText
                    }}>
                      Processed Line Items
                    </Typography>
                    <SearchTable
                      rows={selectedBillingInvoice.processedBilling.map(item => [
                        item.displayItemName || item.billingItemName || '—',
                        item.quantity != null ? item.quantity.toFixed(BILLING_DECIMAL_PLACES) : '—',
                        item.unit || '—',
                        item.unitPrice != null ? `$${item.unitPrice.toFixed(BILLING_DECIMAL_PLACES)}` : '—',
                        item.subtotal != null ? `$${item.subtotal.toFixed(BILLING_DECIMAL_PLACES)}` : '—',
                        item.gstPercentage != null ? `${item.gstPercentage}%` : '—',
                        item.totalAmount != null ? `$${item.totalAmount.toFixed(BILLING_DECIMAL_PLACES)}` : '—',
                        item.remarks
                          ? <span style={{ color: '#d32f2f', fontWeight: 600, whiteSpace: 'nowrap' }}>{item.remarks}</span>
                          : '—',
                      ])}
                      headCells={[
                        { id: 'billingItemName', label: 'Billing Item Name' },
                        { id: 'quantity', label: 'Quantity' },
                        { id: 'unit', label: 'Unit' },
                        { id: 'unitPrice', label: 'Unit Price' },
                        { id: 'amountExGst', label: 'Amount (ex GST)' },
                        { id: 'taxRate', label: 'Tax Rate' },
                        { id: 'amountIncGst', label: 'Amount (inc GST)' },
                        { id: 'remarks', label: 'Remarks' },
                      ]}
                      name="Processed Line Items"
                    />
                  </>
                ) : (
                  <Box p={3} textAlign="center" style={{ background: appTheme.colors.neutral.background, borderRadius: appTheme.borderRadius.small }}>
                    <Typography variant="body2" color="textSecondary">
                      No processed billing items available yet. {selectedBillingInvoice.processedBillingStatus === 'PENDING' && 'Processing has not started.'}
                    </Typography>
                  </Box>
                )}

                <Box mt={3} display="flex" justifyContent="space-between">
                  {selectedBillingInvoice.processedBillingStatus &&
                    selectedBillingInvoice.processedBillingStatus !== 'PENDING' &&
                    selectedBillingInvoice.processedBillingStatus !== 'STARTED' && (
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => {
                          setBillingDetailsModalOpen(false);
                          handleReprocessSingle(selectedBillingInvoice);
                        }}
                      >
                        Re-process
                      </Button>
                    )}
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setBillingDetailsModalOpen(false)}
                    style={{ marginLeft: 'auto' }}
                  >
                    Close
                  </Button>
                </Box>
              </div>
            </div>
          </Fade>
        </Modal>
      )}

      {/* Re-process Modal */}
      <Modal
        className={classes.modal}
        open={reprocessModalOpen}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
        onClose={() => setReprocessModalOpen(false)}
      >
        <Fade in={reprocessModalOpen}>
          <div className={classes.modalPaper} style={{ maxWidth: 500 }}>
            <div className={classes.modalHeader}>
              <Typography variant="h6" style={{ fontWeight: appTheme.typography.fontWeight.bold }}>
                Re-process Billing
              </Typography>
              <IconButton
                size="small"
                onClick={() => setReprocessModalOpen(false)}
                style={{ color: appTheme.colors.primary.contrast }}
              >
                <CloseIcon />
              </IconButton>
            </div>

            <div className={classes.modalBody}>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: appTheme.spacing.lg }}>
                Select which invoices you want to re-process:
              </Typography>

              <Box mb={2}>
                <Button
                  fullWidth
                  variant={reprocessOption === 'all' ? 'contained' : 'outlined'}
                  color="primary"
                  onClick={() => setReprocessOption('all')}
                  style={{ marginBottom: appTheme.spacing.sm, justifyContent: 'flex-start' }}
                >
                  Re-process All
                </Button>
                <Button
                  fullWidth
                  variant={reprocessOption === 'failed' ? 'contained' : 'outlined'}
                  color="primary"
                  onClick={() => setReprocessOption('failed')}
                  style={{ marginBottom: appTheme.spacing.sm, justifyContent: 'flex-start' }}
                >
                  Re-process Failed Only
                </Button>
                <Button
                  fullWidth
                  variant={reprocessOption === 'selected' ? 'contained' : 'outlined'}
                  color="primary"
                  onClick={() => setReprocessOption('selected')}
                  style={{ justifyContent: 'flex-start' }}
                  disabled
                >
                  Re-process Selected (Coming Soon)
                </Button>
              </Box>

              <Box mt={3} display="flex" justifyContent="space-between">
                <Button
                  variant="outlined"
                  onClick={() => setReprocessModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleReprocessConfirm}
                >
                  Confirm Re-process
                </Button>
              </Box>
            </div>
          </div>
        </Fade>
      </Modal>

      <div className={classes.headerRow}>
        <div>
          <Typography variant="h5" style={{ marginBottom: appTheme.spacing.xs, fontWeight: appTheme.typography.fontWeight.bold, color: appTheme.colors.neutral.darkText }}>
            Billing Run
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Run Id: <code>{processId}</code>
          </Typography>
        </div>

        <div className={classes.chipsRow}>
          <Chip
            size="small"
            label={`Step 1: ${step1Status || 'Loading…'}`}
            color={step1StatusChipColor}
          />
          <Chip
            size="small"
            variant="outlined"
            label={`Period: ${formatInvoiceDate(invoiceDownloadProgress?.invoiceDate) || '—'}`}
          />
        </div>

        <Button
          variant="outlined"
          color="default"
          onClick={() => props.history.push('/billing/billing-runs')}
        >
          Back
        </Button>
      </div>

      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="primary">
              <Typography variant="h6" style={{ margin: 0, color: appTheme.colors.primary.contrast, fontWeight: appTheme.typography.fontWeight.semibold }}>
                Billing Run Workflow
              </Typography>
              <Typography variant="body2" style={{ margin: 0, color: 'rgba(255,255,255,0.85)' }}>
                Complete 3-step process: Download → Process → Export
              </Typography>
            </CardHeader>
            <CardBody>
              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                indicatorColor="primary"
                textColor="primary"
                style={{ marginBottom: appTheme.spacing.md }}
              >
                <Tab
                  label={
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                      <CloudDownloadIcon fontSize="small" />
                      <span>Step 1: Download</span>
                    </Box>
                  }
                />
                <Tab
                  label={
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                      {invoiceDownloadProgress?.billingProcessStatus === 'STARTED' ? (
                        <CircularProgress size={16} thickness={5} style={{ color: appTheme.colors.primary }} />
                      ) : (
                        <AssignmentIcon fontSize="small" />
                      )}
                      <span>Step 2: Process</span>
                    </Box>
                  }
                />
                <Tab
                  label={
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                      <PublishIcon fontSize="small" />
                      <span>Step 3: Export</span>
                    </Box>
                  }
                />
              </Tabs>
              <Divider style={{ marginBottom: appTheme.spacing.lg }} />

              {/* ══════════════════ TAB 0 — STEP 1: DOWNLOAD ══════════════════ */}
              {tab === 0 && (
                <>
                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">
                      Step 1 downloads raw billing data from Soracom API for all selected operators.
                      Completed: <strong>{step1Totals.completed}</strong> • Errors: <strong>{step1Totals.errors}</strong>
                    </Typography>
                  </Box>

                  {invoiceDownloadProgress && ['PENDING', 'STARTED'].includes(invoiceDownloadProgress.downloadStatus) ? (
                    <div className={classes.progressRow}>
                      <LinearProgress />
                      <Typography variant="caption" color="textSecondary">
                        Downloading in progress… (auto-refreshing)
                      </Typography>
                    </div>
                  ) : null}

                  <SearchTable
                    rows={allInvoices.map(inv => ([
                      inv.name,
                      inv.status,
                      inv.operatorId,
                      // (inv.total || 0).toFixed(BILLING_DECIMAL_PLACES),
                      new Date(inv.createdAt)
                    ]))}
                    headCells={invoicesHeadCells}
                    name="Step 1 Results"
                    onClick={handleInvoiceRowClick}
                  />
                </>
              )}

              {/* ══════════════════ TAB 1 — STEP 2: PROCESSING ══════════════════ */}
              {tab === 1 && (
                <>
                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">
                      Step 2 processes raw billing data, maps Soracom items to master billing items, and generates customer invoices.
                      This step becomes available once Step 1 is completed.
                    </Typography>
                  </Box>

                  {!canRunStep2 ? (
                    <Box p={4} textAlign="center" style={{ background: appTheme.colors.status.warning.light, borderRadius: appTheme.borderRadius.small }}>
                      <Typography variant="body1" color="textSecondary" style={{ marginBottom: appTheme.spacing.sm }}>
                        Step 2 is not available yet
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Complete Step 1 (Download) first. Once Step 1 is completed, you can process the billing data here.
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      {!invoiceDownloadProgress?.billingProcessStatus || invoiceDownloadProgress.billingProcessStatus === 'PENDING' ? (
                        <div className={classes.actionsRow}>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={startBillingProcessing}
                            startIcon={<AssignmentIcon />}
                          >
                            Start Processing All
                          </Button>
                        </div>
                      ) : null}

                      {invoiceDownloadProgress?.billingProcessStatus === 'STARTED' && (
                        <div className={classes.progressRow}>
                          <Box mb={2} p={2} style={{ background: appTheme.colors.primary.light, borderRadius: appTheme.borderRadius.small }}>
                            <Typography variant="body2" style={{ color: appTheme.colors.primary.dark, marginBottom: appTheme.spacing.sm }}>
                              🔄 Processing billing data...
                            </Typography>
                            <LinearProgress variant="determinate" value={step2Percent} />
                            <Typography variant="caption" color="textSecondary" style={{ marginTop: appTheme.spacing.xs, display: 'block' }}>
                              {step2Totals.completed} completed • {step2Totals.errors} errors • {step2Percent}% complete
                            </Typography>
                          </Box>
                          <Box mb={2}>
                            <Button
                              variant="outlined"
                              color="secondary"
                              onClick={handleAbortProcessing}
                              size="small"
                            >
                              Abort Processing
                            </Button>
                          </Box>
                        </div>
                      )}

                      {invoiceDownloadProgress?.billingProcessStatus === 'COMPLETED' && (() => {
                        const partialCount = (allInvoices || []).filter(inv => inv.processedBillingStatus === 'PARTIAL_WITH_REVIEW').length;
                        const fullyDone = step2Totals.completed - partialCount;
                        return (
                          <Box mt={2} p={2} style={{ background: appTheme.colors.status.success.light, borderRadius: appTheme.borderRadius.small }}>
                            <Typography variant="body2" style={{ color: appTheme.colors.status.success.dark }}>
                              ✓ Processing complete! {fullyDone} invoices processed successfully
                              {partialCount > 0 && `, ${partialCount} processed with items needing review`}.
                              Proceed to Step 3 for export.
                            </Typography>
                            {partialCount > 0 && (
                              <Typography variant="body2" style={{ color: appTheme.colors.status.warning.dark, marginTop: appTheme.spacing.xs }}>
                                ⚠ {partialCount} invoice{partialCount > 1 ? 's have' : ' has'} unmatched billing items. Go to <strong>Master Sheet → Requires Review</strong> tab to resolve them.
                              </Typography>
                            )}
                          </Box>
                        );
                      })()}

                      {invoiceDownloadProgress?.billingProcessStatus === 'COMPLETED_WITH_ERRORS' && (
                        <Box mt={2} p={2} style={{ background: appTheme.colors.status.warning.light, borderRadius: appTheme.borderRadius.small }}>
                          <Typography variant="body2" style={{ color: appTheme.colors.status.warning.dark }}>
                            ⚠ Processing completed with errors. {step2Totals.completed} succeeded, {step2Totals.errors} failed.
                          </Typography>
                        </Box>
                      )}

                      {/* Bulk Re-process Actions */}
                      {invoiceDownloadProgress?.billingProcessStatus &&
                        invoiceDownloadProgress.billingProcessStatus !== 'PENDING' &&
                        invoiceDownloadProgress.billingProcessStatus !== 'STARTED' && (
                          <Box mt={2} mb={2}>
                            <Button
                              variant="outlined"
                              color="primary"
                              onClick={openReprocessModal}
                              startIcon={<AssignmentIcon />}
                            >
                              Re-process Billing
                            </Button>
                          </Box>
                        )}

                      <SearchTable
                        rows={(allInvoices || []).map((invoice, idx) => ([
                          invoice.operatorId || '—',
                          invoice.status || '—',
                          invoice.processedBillingStatus || 'PENDING',
                          invoice.processedBilling?.length || 0,
                          invoice.processedBillingTotal != null ? `$${invoice.processedBillingTotal.toFixed(BILLING_DECIMAL_PLACES)}` : '—',
                        ]))}
                        headCells={[
                          { id: 'operatorId', numeric: false, disablePadding: false, label: 'Operator ID' },
                          { id: 'status', numeric: false, disablePadding: false, label: 'Download Status' },
                          { id: 'processedBillingStatus', numeric: false, disablePadding: false, label: 'Processing Status' },
                          { id: 'processedItems', numeric: true, disablePadding: false, label: 'Processed Items' },
                          { id: 'processedBillingTotal', numeric: false, disablePadding: false, label: 'Processed Total' },
                        ]}
                        name="Step 2 Results"
                        onClick={handleBillingRowClick}
                      />
                    </>
                  )}
                </>
              )}

              {/* ══════════════════ TAB 2 — STEP 3: EXPORT ══════════════════ */}
              {tab === 2 && (
                <>
                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">
                      Step 3 exports processed billing data as CSV files in a ZIP archive.
                      This step becomes available once Step 2 is completed.
                    </Typography>
                  </Box>

                  {(!['COMPLETED', 'COMPLETED_WITH_ERRORS'].includes(invoiceDownloadProgress?.billingProcessStatus)
                    || !(allInvoices && allInvoices.every(inv => ['COMPLETED', 'BILLING_DISABLED', 'NO_INVOICE'].includes(inv.processedBillingStatus)))
                  ) ? (
                    <Box p={4} textAlign="center" style={{ background: appTheme.colors.status.warning.light, borderRadius: appTheme.borderRadius.small }}>
                      <Typography variant="body1" color="textSecondary" style={{ marginBottom: appTheme.spacing.sm }}>
                        Step 3 is not available yet
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Complete Step 2 (Processing) for all invoices before exporting.
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <div className={classes.actionsRow}>
                        <Button
                          variant="contained"
                          color="primary"
                          disabled={exportInProgress}
                          onClick={startBillingExport}
                          startIcon={exportInProgress ? <CircularProgress size={20} /> : <PublishIcon />}
                        >
                          {exportInProgress ? 'Preparing Export...' : 'Create New Export'}
                        </Button>
                      </div>

                      {exports.length > 0 ? (
                        <Box mt={3}>
                          <Typography variant="subtitle1" style={{ marginBottom: appTheme.spacing.sm, fontWeight: appTheme.typography.fontWeight.semibold }}>
                            Export History
                          </Typography>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Created</TableCell>
                                  <TableCell>Status</TableCell>
                                  <TableCell>Total Amount</TableCell>
                                  <TableCell>Invoices</TableCell>
                                  <TableCell>File Size</TableCell>
                                  <TableCell>Actions</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {exports.map((exp) => (
                                  <TableRow key={exp._id}>
                                    <TableCell>
                                      {new Date(exp.createdAt).toLocaleString()}
                                    </TableCell>
                                    <TableCell>
                                      {exp.status === 'PENDING' && '⏳ Pending'}
                                      {exp.status === 'PROCESSING' && '🔄 Processing'}
                                      {exp.status === 'COMPLETED' && '✅ Completed'}
                                      {exp.status === 'FAILED' && '❌ Failed'}
                                    </TableCell>
                                    <TableCell>
                                      ${(exp.totalAmount || 0).toFixed(BILLING_DECIMAL_PLACES)}
                                    </TableCell>
                                    <TableCell>
                                      {exp.invoiceCount || 0}
                                    </TableCell>
                                    <TableCell>
                                      {exp.zipFile?.fileSizeBytes
                                        ? `${(exp.zipFile.fileSizeBytes / 1024 / 1024).toFixed(2)} MB`
                                        : '—'}
                                    </TableCell>
                                    <TableCell>
                                      {exp.status === 'COMPLETED' && exp.zipFile ? (
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          disabled={downloadingExportId === exp._id}
                                          startIcon={downloadingExportId === exp._id ? <CircularProgress size={14} thickness={5} /> : <CloudDownloadIcon fontSize="small" />}
                                          onClick={() => downloadExport(exp)}
                                        >
                                          {downloadingExportId === exp._id ? 'Downloading…' : 'Download'}
                                        </Button>
                                      ) : (
                                        <Typography variant="caption" color="textSecondary">
                                          {exp.status === 'FAILED' ? 'Failed' : 'Processing...'}
                                        </Typography>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      ) : (
                        <Box mt={3} p={3} textAlign="center" style={{ background: appTheme.colors.neutral.gray100, borderRadius: appTheme.borderRadius.small }}>
                          <Typography variant="body2" color="textSecondary">
                            No exports created yet. Click "Create New Export" to generate a ZIP file of all processed invoices.
                          </Typography>
                        </Box>
                      )}
                    </>
                  )}
                </>
              )}
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
    </>
  )
}

export default withBase(connect(null, actions)(BillingRunDetails));
